import { useState, useRef, useEffect } from "react";
import { API } from "constants/RestApi";
import { useAppSelector } from "core/helpers";
import useBaseAction from "./useBaseActions";

/**
 * Custom hook for managing logo in navbar
 *
 * Features:
 * - Fetch logo from API when user is authenticated
 * - Show default logo when user is not authenticated
 * - Auto-refresh when user changes
 * - Memory leak prevention with Object URL cleanup
 */
export const useNavbarLogo = () => {
  const { request } = useBaseAction();
  const { currentUser } = useAppSelector(state => state.app);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isFetching = useRef(false);

  /**
   * Fetches logo from the API
   * @param forceRefresh - Force refresh even if already fetching
   * @returns Promise<string | null> - Logo URL or null
   */
  const fetchLogo = async (forceRefresh = false): Promise<string | null> => {
    // Don't fetch if no user (not authenticated)
    if (!currentUser) {
      return null;
    }

    // Prevent multiple simultaneous requests unless forced
    if (isFetching.current && !forceRefresh) {
      return logoUrl;
    }

    try {
      isFetching.current = true;
      setIsLoading(true);

      // API returns binary image data, not JSON
      // Add cache buster to ensure fresh data
      const cacheBuster = forceRefresh ? `?t=${Date.now()}` : "";
      const response = await request({
        method: "GET",
        url: `${API.SETTINGS_TENANT_LOGO}${cacheBuster}`,
        responseType: "blob", // Important: handle as blob
        useLoader: false, // Don't show global loader for navbar
      });

      // Create object URL from blob for display
      const newLogoUrl = URL.createObjectURL(response);

      // Clean up previous logo URL to prevent memory leaks
      if (logoUrl && logoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(logoUrl);
      }

      setLogoUrl(newLogoUrl);
      return newLogoUrl;
    } catch (err) {
      console.error("Error fetching navbar logo:", err);
      return null;
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  };

  /**
   * Clears logo when user logs out
   */
  const clearLogo = () => {
    if (logoUrl && logoUrl.startsWith("blob:")) {
      URL.revokeObjectURL(logoUrl);
    }
    setLogoUrl(null);
  };

  /**
   * Fetch logo when user changes
   */
  useEffect(() => {
    if (currentUser) {
      // User is authenticated, fetch logo
      fetchLogo();
    } else {
      // User is not authenticated, clear logo
      clearLogo();
    }
  }, [currentUser]);

  /**
   * Cleanup object URLs on unmount
   */
  useEffect(() => {
    return () => {
      if (logoUrl && logoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(logoUrl);
      }
    };
  }, [logoUrl]);

  return {
    logoUrl,
    isLoading,
    fetchLogo,
    clearLogo,
  };
};
